import { useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, Zap } from "lucide-react";
import {
  Alert,
  Button,
  Checkbox,
  DialogClose,
  Field,
  FormDrawer,
  Input,
  SearchInput,
  Segmented,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@h2j-data-team/ui";
import type { StartQvdAnalysisInput } from "@repo/shared";

import { discardMessages } from "@/lib/ui-labels";
import { qvdScopeOptionsQueryOptions } from "@/features/qvd-analysis/api";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";

type ScopeMode = "tenant" | "selected";

export function RunAnalysisDrawer({
  open,
  onOpenChange,
  onRun,
  running,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRun: (input: StartQvdAnalysisInput) => void;
  running: boolean;
  /** The button that opened the drawer, for the focus to return to when it closes. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const [connectionId, setConnectionId] = useState("");
  const [mode, setMode] = useState<ScopeMode>("tenant");
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [spaceSearch, setSpaceSearch] = useState("");
  // Raw comma-separated input; split into the scope's `namePatterns` on run.
  const [namePatterns, setNamePatterns] = useState("");
  const [forceRefresh, setForceRefresh] = useState(false);
  // "What to evaluate" — all on by default (the most thorough analysis); the
  // user unchecks to make a run cheaper/faster.
  const [evaluateTotalSpace, setEvaluateTotalSpace] = useState(true);
  const [evaluateOrphans, setEvaluateOrphans] = useState(true);
  const [evaluateUnusedColumns, setEvaluateUnusedColumns] = useState(true);

  // Every opening starts from the defaults: the form is reset as it closes.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) {
      setConnectionId("");
      setMode("tenant");
      setSelectedSpaces([]);
      setSpaceSearch("");
      setNamePatterns("");
      setForceRefresh(false);
      setEvaluateTotalSpace(true);
      setEvaluateOrphans(true);
      setEvaluateUnusedColumns(true);
    }
  }

  // Closing or leaving with anything changed from the defaults asks first.
  const dirty =
    connectionId !== "" ||
    mode !== "tenant" ||
    namePatterns.trim() !== "" ||
    forceRefresh ||
    !evaluateTotalSpace ||
    !evaluateOrphans ||
    !evaluateUnusedColumns;

  const { data: connections } = useQuery({
    ...connectionsQueryOptions,
    enabled: open,
  });

  const scopeQuery = useQuery({
    ...qvdScopeOptionsQueryOptions(connectionId),
    enabled: open && mode === "selected" && !!connectionId,
  });

  const toggleSpace = (id: string) =>
    setSelectedSpaces((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  // Dependency: unused columns requires orphans. Unchecking orphans cascades
  // down to unused columns.
  const toggleOrphans = (v: boolean) => {
    setEvaluateOrphans(v);
    if (!v) setEvaluateUnusedColumns(false);
  };

  const filteredSpaces = (scopeQuery.data?.spaces ?? []).filter((s) =>
    s.spaceName.toLowerCase().includes(spaceSearch.toLowerCase()),
  );

  const noneSelected = !evaluateTotalSpace && !evaluateOrphans && !evaluateUnusedColumns;
  // Fast scan = only the inventory-only footprint runs (no lineage phases).
  const fastScan = evaluateTotalSpace && !evaluateOrphans && !evaluateUnusedColumns;
  const canRun =
    !!connectionId && !noneSelected && !(mode === "selected" && selectedSpaces.length === 0);

  const handleRun = () => {
    if (!canRun) return;
    const selected = mode === "selected";
    // Patterns narrow every phase (footprint included), so they always apply.
    const patterns = namePatterns
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    // Patterns must work on whole-tenant runs too, so a scope is sent whenever
    // either half is set; tenant + no patterns keeps sending no scope at all.
    const scope =
      selected || patterns.length > 0
        ? {
            spaceIds: selected ? selectedSpaces : [],
            qvdIds: [],
            namePatterns: patterns,
            // Persist the picked space names so the list/detail can show them
            // without a live Qlik lookup later.
            spaces: selected
              ? (scopeQuery.data?.spaces ?? []).filter((s) => selectedSpaces.includes(s.spaceId))
              : [],
          }
        : undefined;
    onRun({
      connectionId,
      scope,
      // The cache toggle only affects the column phase; ignore it when columns
      // don't run so a footprint-only run never carries a stray flag.
      forceFullRefresh: forceRefresh && evaluateUnusedColumns,
      evaluateTotalSpace,
      evaluateOrphans,
      evaluateUnusedColumns,
    });
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t("drawer.title")}
      description={t("drawer.subtitle")}
      closeLabel={tc("close")}
      busy={running}
      dirty={dirty}
      discardConfirmation={discardMessages()}
      restoreFocusRef={restoreFocusRef}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <DialogClose asChild>
            <Button variant="outline">{t("drawer.cancel")}</Button>
          </DialogClose>
          <Button onClick={handleRun} loading={running} disabled={!canRun}>
            {t("drawer.run")}
          </Button>
        </div>
      }
    >
      {/* No <form>: starting an analysis spends quota, so Enter in a field must
          not start one — only the button does. */}
      <div className="flex flex-col gap-5">
        <Field label={t("drawer.connection")}>
          {(control) => (
            <Select
              value={connectionId}
              onValueChange={(v) => {
                setConnectionId(v);
                setSelectedSpaces([]);
              }}
            >
              <SelectTrigger {...control}>
                <SelectValue placeholder={t("drawer.connectionPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {(connections ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field group label={t("drawer.scope")}>
          <Segmented<ScopeMode>
            value={mode}
            onValueChange={setMode}
            options={[
              { value: "tenant", label: t("drawer.wholeTenant") },
              { value: "selected", label: t("drawer.selected") },
            ]}
          />
        </Field>

        {mode === "selected" && (
          <Field group label={t("drawer.spaces")}>
            <div role="group" className="flex flex-col gap-2">
              {!connectionId && (
                <p className="text-sm text-dim">{t("drawer.pickConnectionFirst")}</p>
              )}

              {connectionId && scopeQuery.isLoading && (
                <p role="status" className="flex items-center gap-2 text-sm text-dim">
                  <Loader2 aria-hidden className="size-4 animate-spin" />
                  {t("drawer.loadingScope")}
                </p>
              )}

              {connectionId && scopeQuery.isError && (
                <p role="alert" className="text-sm text-status-danger-foreground">
                  {t("drawer.scopeError")}{" "}
                  <Button variant="link" size="compact" onClick={() => void scopeQuery.refetch()}>
                    {t("drawer.retry")}
                  </Button>
                </p>
              )}

              {connectionId && scopeQuery.data && (
                <>
                  {scopeQuery.data.spaces.length > 0 && (
                    <SearchInput
                      value={spaceSearch}
                      onValueChange={setSpaceSearch}
                      clearLabel={tc("list.clearSearch")}
                      aria-label={t("drawer.searchSpaces")}
                      placeholder={t("drawer.searchSpaces")}
                    />
                  )}
                  <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
                    {scopeQuery.data.spaces.length === 0 ? (
                      <p className="text-sm text-dim">{t("drawer.noSpaces")}</p>
                    ) : filteredSpaces.length === 0 ? (
                      <p className="text-sm text-dim">{t("drawer.noSpaceMatches")}</p>
                    ) : (
                      filteredSpaces.map((s) => (
                        <label
                          key={s.spaceId}
                          className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm"
                        >
                          <Checkbox
                            checked={selectedSpaces.includes(s.spaceId)}
                            onCheckedChange={() => toggleSpace(s.spaceId)}
                          />
                          {s.spaceName}
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </Field>
        )}

        <Field label={t("drawer.namePatterns")} hint={t("drawer.namePatternsHelp")}>
          <Input
            value={namePatterns}
            onChange={(e) => setNamePatterns(e.target.value)}
            placeholder={t("drawer.namePatternsPlaceholder")}
          />
        </Field>

        <Field group label={t("drawer.evaluate.label")}>
          <div role="group" className="flex flex-col gap-3.5">
            <Field
              inline
              label={t("drawer.evaluate.totalSpace")}
              hint={t("drawer.evaluate.totalSpaceHelp")}
            >
              <Checkbox
                checked={evaluateTotalSpace}
                onCheckedChange={(v) => setEvaluateTotalSpace(v === true)}
              />
            </Field>
            <Field
              inline
              label={t("drawer.evaluate.orphans")}
              hint={t("drawer.evaluate.orphansHelp")}
            >
              <Checkbox
                checked={evaluateOrphans}
                onCheckedChange={(v) => toggleOrphans(v === true)}
              />
            </Field>
            {evaluateOrphans && <Alert tone="info">{t("drawer.evaluate.orphansWarning")}</Alert>}
            <Field
              inline
              label={t("drawer.evaluate.unusedColumnsWithSizes")}
              hint={t("drawer.evaluate.unusedColumnsHelp")}
            >
              <Checkbox
                checked={evaluateUnusedColumns}
                onCheckedChange={(v) => setEvaluateUnusedColumns(v === true)}
                disabled={!evaluateOrphans}
              />
            </Field>

            {fastScan && (
              <Alert tone="info" icon={<Zap />}>
                {t("drawer.evaluate.fastScan")}
              </Alert>
            )}
          </div>
        </Field>

        <Field inline label={t("drawer.forceRefresh")} hint={t("drawer.forceRefreshHelp")}>
          <Switch
            checked={forceRefresh && evaluateUnusedColumns}
            onCheckedChange={setForceRefresh}
            disabled={!evaluateUnusedColumns}
          />
        </Field>
      </div>
    </FormDrawer>
  );
}
